import { useState, useCallback } from "react";
import { Camera, X, AlertCircle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";

const MAX_PHOTOS = 30;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = new Set([
  "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif", "image/bmp", "image/tiff", "image/svg+xml",
  "application/pdf",
  "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
]);
const ALLOWED_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "bmp", "tiff", "tif", "svg",
  "pdf", "doc", "docx", "xls", "xlsx", "csv",
]);

const isAllowedFile = (file) => {
  if (file.type && ALLOWED_TYPES.has(file.type)) return true;
  // Fallback: check extension when browser reports empty MIME type
  const ext = (file.name || "").split(".").pop().toLowerCase();
  return ALLOWED_EXTENSIONS.has(ext);
};

// --- Stable UID generator ---
let _uid = 0;
const nextUid = () => `photo-${++_uid}-${Date.now()}`;

const sanitizeName = (name) => {
  if (!name) return "file";
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.substring(0, dot) : name;
  const ext = dot > 0 ? name.substring(dot).toLowerCase() : "";
  return base.replace(/[^\w\-.]/g, "_") + ext;
};

const createEntries = (fileList, currentCount) => {
  const remaining = MAX_PHOTOS - currentCount;
  if (remaining <= 0) {
    toast.error(`Massimo ${MAX_PHOTOS} file consentiti`);
    return [];
  }

  const valid = [];
  const skipped = [];

  for (const f of Array.from(fileList)) {
    if (!isAllowedFile(f)) {
      skipped.push(`${f.name} (tipo non supportato)`);
      continue;
    }
    if (f.size > MAX_FILE_SIZE) {
      skipped.push(`${f.name} (${(f.size / 1024 / 1024).toFixed(1)}MB, max 10MB)`);
      continue;
    }
    if (valid.length >= remaining) {
      skipped.push(`${f.name} (limite ${MAX_PHOTOS} raggiunto)`);
      continue;
    }
    const safeName = sanitizeName(f.name);
    valid.push({ uid: nextUid(), file: f, preview: URL.createObjectURL(f), name: safeName, size: f.size });
  }

  if (skipped.length > 0) {
    toast.warning(`File scartati: ${skipped.join(", ")}`);
  }
  return valid;
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

const isPreviewable = (name) => {
  const ext = (name || "").split(".").pop().toLowerCase();
  return ["jpg","jpeg","png","gif","webp","bmp","tiff","tif","svg","heic","heif"].includes(ext);
};

// --- Photo preview row ---
const PhotoPreview = ({ photos, onRemove }) => {
  if (photos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-3">
      {photos.map((photo) => (
        <div key={photo.uid} className="relative group">
          {isPreviewable(photo.name) ? (
            <img src={photo.preview} alt={photo.name} className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
          ) : (
            <div className="w-20 h-20 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center">
              <span className="text-xs font-bold text-slate-400 uppercase">{(photo.name || "").split(".").pop()}</span>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-1 max-w-[80px] truncate" title={`${photo.name} (${formatSize(photo.size)})`}>{photo.name}</p>
          <button
            type="button"
            onClick={() => onRemove(photo.uid)}
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`remove-photo-${photo.uid}`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
};

// --- Main export ---
export const PhotoUpload = ({ photos, setPhotos }) => {
  const [isDragging, setIsDragging] = useState(false);

  const addPhotos = useCallback((fileList) => {
    const entries = createEntries(fileList, photos.length);
    if (entries.length > 0) setPhotos((prev) => [...prev, ...entries]);
  }, [setPhotos, photos.length]);

  const removePhoto = useCallback((uid) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.uid === uid);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.uid !== uid);
    });
  }, [setPhotos]);

  const isFull = photos.length >= MAX_PHOTOS;

  return (
    <div className="space-y-2 opacity-0 animate-fade-in animation-delay-500">
      <Label className="label-custom flex items-center gap-2">
        <Camera className="w-4 h-4" />
        Carica File
        <span className="text-xs font-normal text-slate-400 ml-1">({photos.length}/{MAX_PHOTOS})</span>
      </Label>
      <div className="space-y-3">
        <label
          htmlFor="photo-upload"
          className={`flex items-center justify-center w-full h-28 border-2 border-dashed rounded-xl transition-all bg-white ${
            isFull
              ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
              : isDragging ? "border-blue-500 bg-blue-50 scale-[1.01] cursor-pointer" : "border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer"
          }`}
          data-testid="photo-upload-area"
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!isFull) setIsDragging(true); }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!isFull) setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); if (!isFull) addPhotos(e.dataTransfer.files); }}
        >
          <div className="flex flex-col items-center gap-1.5 text-slate-500">
            {isFull ? (
              <>
                <AlertCircle className="w-6 h-6 text-slate-400" />
                <span className="text-sm text-slate-400">Limite di {MAX_PHOTOS} file raggiunto</span>
              </>
            ) : (
              <>
                <Camera className={`w-6 h-6 transition-colors ${isDragging ? "text-blue-500" : ""}`} />
                <span className="text-sm">{isDragging ? "Rilascia i file qui" : "Trascina i file qui o clicca per caricare"}</span>
                <span className="text-xs text-slate-400">JPG, PNG, GIF, SVG, PDF, DOC, XLS, CSV — max 10MB per file</span>
              </>
            )}
          </div>
          <input
            id="photo-upload"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif,image/svg+xml,application/pdf,.doc,.docx,.xls,.xlsx,.csv"
            multiple
            className="hidden"
            disabled={isFull}
            data-testid="photo-input"
            onChange={(e) => { addPhotos(e.target.files || []); e.target.value = ""; }}
          />
        </label>
        <PhotoPreview photos={photos} onRemove={removePhoto} />
      </div>
    </div>
  );
};
